import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Form, Button, Alert, Card } from 'react-bootstrap';
import moment from 'moment';
import ComebackArrow from '../../components/ComebackArrow/ComebackArrow';
import { alertService } from '../../services/AlertService';
import EsamiUtentiService from '../../services/EsamiUtentiService/EsamiUtentiService';
import useMediaQuery from '../../hooks/useMediaQuery';
import IconWithTooltip from '../../components/IconWithTooltip/IconWithTooltip';
import CustomButton from '../../components/CustomButton/CustomButton';
import useAttestati from '../../hooks/useAttestati';

const GestioneRisultatiEsamePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { examData, classeData } = location.state || {};
  const [corsistiPrenotati, setCorsistiPrenotati] = useState([]);
  const [statinoFiles, setStatinoFiles] = useState({});
  const [existingStatini, setExistingStatini] = useState({});
  const isWideScreen = useMediaQuery('(min-width: 1280px)');
  const [isLoading, setIsLoading] = useState(false);
  const { downloadAttestato, loadingStateAttestati } = useAttestati();

  useEffect(() => {
    if (!examData) {
      navigate('/configura-esami');
      return;
    }
    setCorsistiPrenotati(examData.utenti.filter(u => u.prenotato));
    const existingFiles = {};
    examData.utenti.forEach(u => {
      if (u.file_statino) {
        existingFiles[u.id_calendario_esame_utente] = u.file_statino.split('/').pop();
      }
    });
    setExistingStatini(existingFiles);
  }, [examData, navigate]);

  const handleInputChange = (idUtente, field, value) => {
    setCorsistiPrenotati(prev => prev.map(u => 
      u.id_utente === idUtente ? { ...u, [field]: value } : u
    ));
  };

  const handleStatinoChange = (idCalendarioEsameUtente, file) => {
    setStatinoFiles(prev => ({
      ...prev,
      [idCalendarioEsameUtente]: file
    }));
    setExistingStatini(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
  };

  const handleRemoveStatino = (idCalendarioEsameUtente) => {
    setStatinoFiles(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
    setExistingStatini(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
  };

  const handleRemoveExistingStatino = (idCalendarioEsameUtente) => {
    setExistingStatini(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
  };

  const handlePresenzaChange = (idUtente, value) => {
    setCorsistiPrenotati(prev => prev.map(u => 
      u.id_utente === idUtente ? { ...u, presenza: value } : u
    ));
  };

  const renderPresenzaSwitch = (corsista) => (
    <Form.Check 
      type="switch"
      id={`presenza-${corsista.id_utente}`}
      label={corsista.presenza === 'presente' ? 'Presente' : 'Assente'}
      checked={corsista.presenza === 'presente'}
      onChange={(e) => handlePresenzaChange(corsista.id_utente, e.target.checked ? 'presente' : 'assente')}
    />
  );

  const handleSaveResults = async () => {
    setIsLoading(true);
    try {
      const esitiToSend = corsistiPrenotati.map(corsista => ({
        ...corsista,
        presenza: corsista.presenza === 'non_registrato' ? 'assente' : corsista.presenza
      }));
      await EsamiUtentiService.updateEsameUtentiResults({esiti: esitiToSend});
      for (const corsista of corsistiPrenotati) {
        if (corsista.presenza === 'presente') {
          const idCalendarioEsameUtente = corsista.id_calendario_esame_utente;
          if (statinoFiles[idCalendarioEsameUtente]) {
            const formData = new FormData();
            formData.append('statino', statinoFiles[idCalendarioEsameUtente]);
            formData.append('id_calendario_esame_utente', idCalendarioEsameUtente);
            await EsamiUtentiService.uploadStatino(formData);
          } else if (!existingStatini[idCalendarioEsameUtente] && corsista.file_statino) {
            await EsamiUtentiService.removeStatino({ id_calendario_esame_utente: idCalendarioEsameUtente });
          }
        }
      }
      alertService.success('Risultati e statini salvati con successo', { autoClose: true });
      navigate('/configura-esami', { state: { classeData } });
    } catch (error) {
      alertService.error('Errore nel salvataggio dei risultati e degli statini', { autoClose: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadStatino = async (idCalendarioEsameUtente, filename) => {
    try {
      await EsamiUtentiService.downloadStatino({ id_calendario_esame_utente: idCalendarioEsameUtente, filename: filename });
    } catch (error) {
      alertService.error('Errore nel download dello statino');
    }
  };

  const renderCorsistaCard = (corsista) => (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>{corsista.nome} {corsista.cognome}</Card.Title>
        <Form>
          <Form.Group className="mb-3">
            {renderPresenzaSwitch(corsista)}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Esito</Form.Label>
            <Form.Select
              value={corsista.esito_esame}
              onChange={(e) => handleInputChange(corsista.id_utente, 'esito_esame', e.target.value)}
              disabled={corsista.presenza !== 'presente'}
            >
              <option value="non_svolto">Non svolto</option>
              <option value="promosso">Promosso</option>
              <option value="bocciato">Bocciato</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Voto</Form.Label>
            <Form.Control
              type="text"
              value={corsista.voto || ''}
              onChange={(e) => handleInputChange(corsista.id_utente, 'voto', e.target.value)}
              disabled={corsista.presenza !== 'presente' || corsista.esito_esame === 'non_svolto'}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={corsista.note || ''}
              onChange={(e) => handleInputChange(corsista.id_utente, 'note', e.target.value)}
              disabled={corsista.presenza !== 'presente'}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Statino</Form.Label>
            {renderStatinoActions(corsista)}
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderStatinoActions = (corsista) => {
    if (corsista.presenza !== 'presente') {
      return <span>-</span>;
    }

    if (existingStatini[corsista.id_calendario_esame_utente]) {
      return (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <IconWithTooltip
            icon="bi-file-earmark-pdf"
            text="Scarica Statino"
            onClick={() => handleDownloadStatino(corsista.id_calendario_esame_utente, "statino.pdf")}
            variant='danger'
            className="p-0 fs-5"
          />
          <IconWithTooltip
            icon="bi-trash"
            text="Elimina Statino"
            onClick={() => handleRemoveExistingStatino(corsista.id_calendario_esame_utente)}
            variant="outline-danger"
            className="p-0 fs-6"
          />
        </div>
      );
    } else if (statinoFiles[corsista.id_calendario_esame_utente]) {
      return (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <IconWithTooltip
             icon="bi-cloud-upload-fill"
             text="Pronto per caricamento"
             variant="warning"
             className="p-0 fs-5"
          />
          <IconWithTooltip
            icon="bi-x-lg"
            text="Annulla upload"
            onClick={() => handleRemoveStatino(corsista.id_calendario_esame_utente)}
            variant="secondary"
            className="p-0 fs-6"
          />
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <label htmlFor={`statino-upload-${corsista.id_calendario_esame_utente}`} style={{cursor: 'pointer'}}>
             <IconWithTooltip
                icon="bi-upload"
                text="Carica Statino"
                variant="primary"
                className="p-0 fs-5"
             />
          </label>
          <input
            id={`statino-upload-${corsista.id_calendario_esame_utente}`}
            type="file"
            accept=".pdf"
            onChange={(e) => handleStatinoChange(corsista.id_calendario_esame_utente, e.target.files[0])}
            style={{ display: 'none' }}
          />
        </div>
      );
    }
  };

  const handleDownloadAttestato = async (corsista) => {
    if (!corsista.id_classe_utente) {
        alertService.error("Dati mancanti per scaricare l'attestato.");
        return;
    }
    const filename = `attestato_${corsista.cognome}_${corsista.nome}.pdf`;
    await downloadAttestato({ id_classe_utente: corsista.id_classe_utente }, filename);
  };

  const renderAttestatoActions = (corsista) => {
    if (corsista.esito_esame === 'promosso' && corsista.id_attestato) {
        return (
             <div className="d-flex justify-content-center align-items-center">
                <IconWithTooltip
                    icon="bi-file-earmark-text"
                    text="Scarica Attestato"
                    onClick={() => handleDownloadAttestato(corsista)}
                    variant="success"
                    className="p-0 fs-5"
                />
             </div>
        );
    }
    return <div className="text-center text-muted">-</div>;
  };

  const renderContent = () => {
    // 1. Caso Nessun Prenotato
    if (corsistiPrenotati.length === 0) {
      return (
        <Alert variant="secondary" className="mt-4">
          <Alert.Heading>Nessun utente prenotato</Alert.Heading>
          <p>
            Non ci sono utenti prenotati per questo esame. 
            Gli studenti possono prenotarsi all'esame attraverso la loro area personale.
          </p>
          <hr />
          <p className="mb-0">
            Controlla più tardi per eventuali aggiornamenti o contatta il supporto se ritieni che ci sia un errore.
          </p>
        </Alert>
      );
    }

    // 2. Tabella Risultati
    return (
      <>
        <Alert variant="secondary" className='mt-3'>
          Inserisci i risultati dell'esame per ciascun corsista. Assicurati di caricare lo statino per ogni studente presente.
        </Alert>

        <div className='mt-4'>
          {isWideScreen ? (
            <Table responsive striped bordered hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th style={{width: '20%'}}>Corsista</th>
                  <th style={{width: '10%'}}>Presenza</th>
                  <th style={{width: '15%'}}>Esito</th>
                  <th style={{width: '10%'}}>Voto</th>
                  <th style={{width: '25%'}}>Note</th>
                  <th className="text-center" style={{width: '10%'}}>Statino</th>
                  <th className="text-center" style={{width: '10%'}}>Attestato</th> {/* <--- NUOVA COLONNA */}
                </tr>
              </thead>
              <tbody>
                {corsistiPrenotati.map((corsista) => (
                  <tr key={corsista.id_utente}>
                    <td className="fw-bold">{corsista.nome} {corsista.cognome}</td>
                    <td>
                      {renderPresenzaSwitch(corsista)}
                    </td>
                    <td>
                      <Form.Select
                        value={corsista.esito_esame}
                        onChange={(e) => handleInputChange(corsista.id_utente, 'esito_esame', e.target.value)}
                        disabled={corsista.presenza !== 'presente'}
                        size="sm"
                        className={corsista.esito_esame === 'promosso' ? 'border-success text-success fw-bold' : ''}
                      >
                        <option value="non_svolto">Non svolto</option>
                        <option value="promosso">Promosso</option>
                        <option value="bocciato">Bocciato</option>
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        type="text" // Cambiato a text per gestire meglio i caratteri non numerici iniziali
                        value={corsista.voto || ''}
                        onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')} // Solo numeri
                        onChange={(e) => handleInputChange(corsista.id_utente, 'voto', e.target.value)}
                        disabled={corsista.presenza !== 'presente' || corsista.esito_esame === 'non_svolto'}
                        style={{ width: '100%' }}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        value={corsista.note || ''}
                        onChange={(e) => handleInputChange(corsista.id_utente, 'note', e.target.value)}
                        disabled={corsista.presenza !== 'presente'}
                        size="sm"
                      />
                    </td>
                    <td className='text-center'>
                      {renderStatinoActions(corsista)}
                    </td>
                    <td className='text-center'>
                      {renderAttestatoActions(corsista)} {/* <--- NUOVA AZIONE */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            // Versione Mobile (Card)
            corsistiPrenotati.map((corsista) => renderCorsistaCard(corsista))
          )}
        </div>

        <div className='mt-4 text-end'>
          <CustomButton 
            onClick={handleSaveResults}
            isLoading={isLoading}
            label="Salva Risultati"
            size="lg"
          />
        </div>
      </>
    );
  };

  return (
    <div className={`card custom-card w-100 shadow ${isWideScreen ? 'p-5' : 'px-1 py-3'}`} style={{minHeight: '700px'}}>
      <ComebackArrow path="/configura-esami" state={{ classeData }} />
      <div className='container'>
        <div className='d-flex justify-content-between custom-card-header'>
          <div className='d-flex gap-3 custom-card-title my-2'>
            <div className={`custom-icon-container`}>
              {classeData?.corso?.immagine_corso ? 
                <img src={classeData?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className={`bi custom-icon bi-calendar-event fs-2`}></i>}
            </div>
            <div className='align-self-center'>
              <span className='fs-4 d-block'>
                <i><b>{classeData.corso.denominazione} - </b></i>
                Esame del {moment(examData.data).format('DD/MM/YYYY')}, 
                Orario: {examData.ora_inizio.slice(0, 5)} - {examData.ora_fine.slice(0, 5)}
              </span>
            </div>
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default GestioneRisultatiEsamePage;
